import { components } from "@/api-schema";
import { mapAttributeValueToName } from "@/common/helpers/map-attribute-value-to-name";
import { DeviceType } from "@/common/hooks/use-device";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import i18next from "i18next";
import { Info } from "lucide-react";
import { ImplementedAccessorColumnDef } from "../types";
import { commonColumns } from "./_constants/common-columns";
import { AppRoleType } from "./app-role-table";

export type AppRoleTableType = components["schemas"]["AppRole"] & {
  appRoles?: components["schemas"]["AppRole"][];
  attribute?: unknown;
  type?: AppRoleType;
};
type AppRoles = components["schemas"]["AppRole"][];

export const appRoleTableDefaultColumns = {
  columnName: {
    ...commonColumns.columnName<AppRoleTableType>(),
    size: 1000,
    header: () => i18next.t("Name"),
    cell: (row) => {
      const name = row.getValue() as string;
      const v1Id = row.row.original.v1Id;

      if ((row.row.original as AppRoleTableType).type === AppRoleType.Protected) {
        return (
          <div className="flex items-center gap-1">
            {mapAttributeValueToName(name)}
            <Tooltip>
              <TooltipTrigger>
                <Info className="h-4" />
              </TooltipTrigger>
              <TooltipContent side="right">{i18next.t("ram:ProtectedAppRoleInfo")}</TooltipContent>
            </Tooltip>
          </div>
        );
      }

      return (
        <div>
          {mapAttributeValueToName(name)}
          {v1Id ? <span className="text-muted-foreground ml-2 text-xs">(V1: {v1Id})</span> : null}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<AppRoleTableType, unknown>,
  columnSubRoles: {
    id: "subRoles",
    size: 500,
    header: () => i18next.t("SubRoles"),
    enableSorting: false,
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.appRoles,
    cell: (row) => {
      const subRoles = row.getValue() as components["schemas"]["AppRole"][] | undefined;
      if (!subRoles || subRoles.length === 0) {
        return (
          <div className="text-left">
            <EmptyValue />
          </div>
        );
      }
      return (
        <div className="flex flex-wrap gap-1">
          {subRoles.slice(0, 3).map((appRole) => (
            <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary} key={appRole.uuid}>
              {appRole.name}
            </Badge>
          ))}
          {subRoles.length > 3 && (
            <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary}>
              {i18next.t("MoreCount", { count: subRoles.length - 3 })}
            </Badge>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<AppRoleTableType, AppRoles | undefined>,
  columnUuid: commonColumns.columnUuid<AppRoleTableType>(),
  columnV1Id: commonColumns.columnV1Id<AppRoleTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<AppRoleTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<AppRoleTableType>(),
};
